import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import {
  FolderOff as EmptyFolderIcon,
  SearchOff as NoResultsIcon,
  ErrorOutline as ErrorIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

interface EmptyStateProps {
  /**
   * Icon variant to display
   */
  variant?: 'empty' | 'no-results' | 'error' | 'info';

  /**
   * Custom icon component (overrides variant)
   */
  icon?: React.ReactNode;

  /**
   * Title text
   */
  title: string;

  /**
   * Description text (optional)
   */
  description?: string;

  /**
   * Action button props (optional)
   */
  action?: {
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
  };

  /**
   * Minimum height (default: 300px)
   */
  minHeight?: number | string;
}

/**
 * Empty State Component
 * Displays friendly message when no data is available
 */
export default function EmptyState({
  variant = 'empty',
  icon,
  title,
  description,
  action,
  minHeight = 300,
}: EmptyStateProps) {
  const getVariantIcon = () => {
    switch (variant) {
      case 'no-results':
        return <NoResultsIcon sx={{ fontSize: 64 }} />;
      case 'error':
        return <ErrorIcon sx={{ fontSize: 64 }} />;
      case 'info':
        return <InfoIcon sx={{ fontSize: 64 }} />;
      default:
        return <EmptyFolderIcon sx={{ fontSize: 64 }} />;
    }
  };

  const getVariantColor = () => {
    switch (variant) {
      case 'error':
        return 'error.main';
      case 'no-results':
        return 'warning.main';
      case 'info':
        return 'info.main';
      default:
        return 'text.disabled';
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight={minHeight}
      p={4}
      textAlign="center"
    >
      <Box sx={{ color: getVariantColor(), mb: 2 }}>
        {icon || getVariantIcon()}
      </Box>

      <Typography variant="h6" gutterBottom color="text.secondary">
        {title}
      </Typography>

      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400 }}>
          {description}
        </Typography>
      )}

      {action && (
        <Button
          variant="contained"
          onClick={action.onClick}
          startIcon={action.icon}
        >
          {action.label}
        </Button>
      )}
    </Box>
  );
}
